import React, { useState, useEffect } from 'react';
import { 
  X, History, Printer, TrendingUp, TrendingDown, 
  Calendar, User, FileText, Layers, Loader2, DollarSign, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { handleImprimirCotizacionTramite } from '../../utils/printTramiteCotizacion.js';

export default function HistorialCotizacionesModal({ isOpen, tramite, onClose }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' | 'compare'
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [compareVersionId, setCompareVersionId] = useState(null);

  useEffect(() => {
    if (isOpen && tramite?.id) {
      loadHistorial();
    }
  }, [isOpen, tramite?.id]);

  const loadHistorial = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tramites/${tramite.id}/cotizacion/historial`);
      const data = res.data || [];
      // Si la API devuelve las cotizaciones, las ordenamos descendente para versiones
      setHistorial(data);
      if (data.length > 0) {
        setSelectedVersionId(data[0].id);
        if (data.length > 1) {
          setCompareVersionId(data[1].id);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar historial de cotizaciones.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !tramite) return null;

  const selectedCot = historial.find(c => c.id === selectedVersionId) || historial[0];
  const compareCot = historial.find(c => c.id === compareVersionId) || (historial.length > 1 ? historial[1] : null);

  const selectedIndex = historial.findIndex(c => c.id === selectedVersionId);
  const compareIndex = historial.findIndex(c => c.id === compareVersionId);

  const versionNumeroSelected = selectedIndex !== -1 ? historial.length - selectedIndex : 1;
  const versionNumeroCompare = compareIndex !== -1 ? historial.length - compareIndex : 1;

  const totalActual = selectedCot ? parseFloat(selectedCot.total) || 0 : 0;
  const totalPrevio = compareCot ? parseFloat(compareCot.total) || 0 : 0;
  const diffTotal = totalActual - totalPrevio;
  const diffPercent = totalPrevio > 0 ? ((diffTotal / totalPrevio) * 100).toFixed(1) : '0';

  const handlePrint = (cot) => {
    if (!cot) return;
    handleImprimirCotizacionTramite(tramite, {
      items: (cot.items || []).filter(i => !i.is_cuantia),
      cuantias: (cot.items || []).filter(i => i.is_cuantia),
      total: cot.total,
      observaciones: cot.observaciones || '',
      descuentoMonto: cot.descuento_monto || 0,
      descuentoDescripcion: cot.descuento_tipo || '',
      repertorios: tramite.repertorios || []
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div 
        className="modal-content animate-fade-in" 
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '1050px', width: '95%', padding: 0, overflow: 'hidden', borderRadius: '16px' }}
      >
        {/* HEADER */}
        <div style={{
          padding: '1.25rem 1.75rem',
          background: '#0f172a',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #1e293b'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ background: '#3b82f620', color: '#60a5fa', padding: '8px', borderRadius: '10px' }}>
              <History size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#f8fafc' }}>
                Historial de Cotizaciones · {tramite.numero_seguimiento}
              </h3>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                Auditoría y comparativa dinámica de versiones arancelarias
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* TABS DE MODO */}
            <div style={{
              display: 'flex',
              background: '#1e293b',
              padding: '3px',
              borderRadius: '8px',
              border: '1px solid #334155'
            }}>
              <button
                type="button"
                onClick={() => setViewMode('preview')}
                style={{
                  background: viewMode === 'preview' ? '#0284c7' : 'transparent',
                  color: '#fff',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
              >
                <FileText size={14} /> Ficha Versión
              </button>

              <button
                type="button"
                disabled={historial.length < 2}
                onClick={() => setViewMode('compare')}
                style={{
                  background: viewMode === 'compare' ? '#4f46e5' : 'transparent',
                  color: historial.length < 2 ? '#64748b' : '#fff',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: historial.length < 2 ? 'not-allowed' : 'pointer',
                  opacity: historial.length < 2 ? 0.5 : 1
                }}
                title={historial.length < 2 ? 'Se requieren al menos 2 versiones para comparar' : 'Comparar versiones'}
              >
                <Layers size={14} /> Comparar (Diff)
              </button>
            </div>

            {/* BOTÓN IMPRIMIR */}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => handlePrint(selectedCot)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#1e293b', border: '1px solid #334155', color: '#f8fafc' }}
              title="Imprimir versión seleccionada"
            >
              <Printer size={14} /> Imprimir V{versionNumeroSelected}
            </button>

            <button 
              type="button" 
              onClick={onClose} 
              style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem', padding: '4px' }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* BODY */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', minHeight: '480px', maxHeight: '72vh' }}>
          
          {/* SIDEBAR VERSIONES */}
          <div style={{
            borderRight: '1px solid var(--border-color)',
            background: '#f8fafc',
            padding: '1.25rem',
            overflowY: 'auto'
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '1rem', letterSpacing: '0.5px' }}>
              Versiones Emitidas ({historial.length})
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#64748b' }}>
                <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontSize: '0.82rem' }}>Cargando versiones...</div>
              </div>
            ) : historial.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#94a3b8', fontSize: '0.85rem' }}>
                No hay cotizaciones registradas para este trámite.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {historial.map((cot, idx) => {
                  const versionNum = historial.length - idx;
                  const isSelected = cot.id === selectedVersionId;
                  const isCompareTarget = cot.id === compareVersionId;
                  const isLatest = idx === 0;

                  return (
                    <div
                      key={cot.id}
                      onClick={() => setSelectedVersionId(cot.id)}
                      style={{
                        padding: '12px 14px',
                        borderRadius: '10px',
                        background: isSelected ? '#eff6ff' : '#fff',
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        boxShadow: isSelected ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '0.9rem', color: isSelected ? '#1d4ed8' : '#0f172a' }}>
                          Versión {versionNum}
                        </strong>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {isLatest && (
                            <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: '4px' }}>
                              Actual
                            </span>
                          )}
                          <span style={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            background: cot.estado === 'pagada' ? '#dcfce7' : (cot.estado === 'enviada' ? '#e0f2fe' : '#fef3c7'),
                            color: cot.estado === 'pagada' ? '#166534' : (cot.estado === 'enviada' ? '#0369a1' : '#92400e'),
                            padding: '2px 6px',
                            borderRadius: '4px',
                            textTransform: 'uppercase'
                          }}>
                            {cot.estado || 'Emitida'}
                          </span>
                        </div>
                      </div>

                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
                        ${new Intl.NumberFormat('es-CL').format(cot.total || 0)} CLP
                      </div>

                      <div style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '6px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={12} /> {cot.created_at ? new Date(cot.created_at).toLocaleString('es-CL') : '-'}
                        </span>
                        {cot.creador?.name && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <User size={12} /> {cot.creador.name}
                          </span>
                        )}
                      </div>

                      {/* Selector para modo comparar */}
                      {viewMode === 'compare' && (
                        <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Comparar con:</span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCompareVersionId(cot.id);
                            }}
                            style={{
                              padding: '2px 8px',
                              fontSize: '0.7rem',
                              background: isCompareTarget ? '#4f46e5' : '#f1f5f9',
                              color: isCompareTarget ? '#fff' : '#475569',
                              border: 'none',
                              borderRadius: '4px'
                            }}
                          >
                            {isCompareTarget ? '✓ Base Comparación' : 'Fijar como Base'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* MAIN VIEW */}
          <div style={{ padding: '1.5rem 2rem', overflowY: 'auto', background: '#fff' }}>
            
            {/* MODO PREVIEW: FICHA INSTITUCIONAL DE LA VERSIÓN */}
            {viewMode === 'preview' && selectedCot && (
              <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', paddingBottom: '1rem', borderBottom: '2px solid #0f172a', marginBottom: '1.25rem' }}>
                  <h4 style={{ margin: '0 0 4px', fontSize: '1.1rem', fontWeight: 800, textTransform: 'uppercase', color: '#0f172a' }}>
                    Conservador de Bienes Raíces de San Carlos
                  </h4>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Cotización de Derechos Arancelarios · Versión {versionNumeroSelected}
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '0.8rem',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <span style={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Folio Trámite:</span>
                    <strong style={{ color: '#0f172a' }}>{tramite.numero_seguimiento}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Solicitante:</span>
                    <strong style={{ color: '#0f172a' }}>{tramite.nombre_solicitante || '-'}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Fecha de Emisión:</span>
                    <strong style={{ color: '#0f172a' }}>
                      {selectedCot.created_at ? new Date(selectedCot.created_at).toLocaleString('es-CL') : '-'}
                    </strong>
                  </div>
                </div>

                <h5 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', margin: '0 0 8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                  Detalle de Actos e Ítems Cotizados
                </h5>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '1.5rem' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', textAlign: 'left', color: '#334155' }}>
                      <th style={{ padding: '8px 10px' }}>Descripción</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', width: '60px' }}>Cant.</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', width: '110px' }}>Unitario</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', width: '120px' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedCot.items || []).filter(i => (parseFloat(i.monto) > 0 || parseFloat(i.precio_unitario) > 0)).map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', color: '#1e293b' }}>
                          {it.is_cuantia && (
                            <span style={{ fontSize: '0.65rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', marginRight: '6px', fontWeight: 700 }}>
                              CUANTÍA
                            </span>
                          )}
                          {it.descripcion}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', color: '#64748b' }}>{it.cantidad || 1}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>
                          ${new Intl.NumberFormat('es-CL').format(it.precio_unitario || 0)}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                          ${new Intl.NumberFormat('es-CL').format(it.monto || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* TOTAL */}
                <div style={{
                  background: '#0f172a',
                  color: '#fff',
                  borderRadius: '10px',
                  padding: '14px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Total Derechos Arancelarios:
                  </span>
                  <span style={{ fontSize: '1.4rem', fontWeight: 900, color: '#38bdf8' }}>
                    ${new Intl.NumberFormat('es-CL').format(selectedCot.total || 0)} CLP
                  </span>
                </div>

                {selectedCot.observaciones && (
                  <div style={{ marginTop: '1.25rem', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.8rem', color: '#475569', fontStyle: 'italic' }}>
                    <strong>Observaciones:</strong> {selectedCot.observaciones}
                  </div>
                )}
              </div>
            )}

            {/* MODO COMPARE: MATRIZ DIFF SIDE-BY-SIDE */}
            {viewMode === 'compare' && selectedCot && compareCot && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                
                {/* KPI DIFF BANNER */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                  <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                      Versión Base (V{versionNumeroCompare})
                    </span>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#334155', marginTop: '4px' }}>
                      ${new Intl.NumberFormat('es-CL').format(totalPrevio)} CLP
                    </div>
                  </div>

                  <div style={{ background: '#eff6ff', padding: '12px 16px', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase' }}>
                      Versión Seleccionada (V{versionNumeroSelected})
                    </span>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#1e3a8a', marginTop: '4px' }}>
                      ${new Intl.NumberFormat('es-CL').format(totalActual)} CLP
                    </div>
                  </div>

                  <div style={{
                    background: diffTotal >= 0 ? '#fef3c7' : '#ecfdf5',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${diffTotal >= 0 ? '#fde68a' : '#a7f3d0'}`
                  }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: diffTotal >= 0 ? '#92400e' : '#065f46', textTransform: 'uppercase' }}>
                      Diferencial Arancelario
                    </span>
                    <div style={{
                      fontSize: '1.15rem',
                      fontWeight: 800,
                      color: diffTotal >= 0 ? '#b45309' : '#047857',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      {diffTotal >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                      {diffTotal >= 0 ? `+` : ``}${new Intl.NumberFormat('es-CL').format(diffTotal)} CLP ({diffPercent}%)
                    </div>
                  </div>
                </div>

                {/* MATRIZ DE COMPARACIÓN DE ITEMS */}
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: '0.8rem', color: '#334155' }}>
                    Comparativa de Ítems: V{versionNumeroCompare} vs V{versionNumeroSelected}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', fontSize: '0.8rem' }}>
                    {/* Columna Base */}
                    <div style={{ padding: '14px', borderRight: '1px solid #e2e8f0', background: '#fafafa' }}>
                      <div style={{ fontWeight: 700, color: '#475569', marginBottom: '8px' }}>
                        V{versionNumeroCompare} ({compareCot.created_at ? new Date(compareCot.created_at).toLocaleDateString('es-CL') : '-'})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {(compareCot.items || []).filter(i => parseFloat(i.monto) > 0).map((it, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', background: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <span style={{ color: '#334155' }}>{it.descripcion} (x{it.cantidad || 1})</span>
                            <strong>${new Intl.NumberFormat('es-CL').format(it.monto || 0)}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Columna Actual con Diffs */}
                    <div style={{ padding: '14px', background: '#fff' }}>
                      <div style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: '8px' }}>
                        V{versionNumeroSelected} ({selectedCot.created_at ? new Date(selectedCot.created_at).toLocaleDateString('es-CL') : '-'})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {(selectedCot.items || []).filter(i => parseFloat(i.monto) > 0).map((it, idx) => {
                          const previo = (compareCot.items || []).find(pi => pi.descripcion === it.descripcion);
                          const esNuevo = !previo;
                          const haCambiado = previo && parseFloat(previo.monto) !== parseFloat(it.monto);

                          return (
                            <div 
                              key={idx} 
                              style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                padding: '6px 8px', 
                                borderRadius: '6px', 
                                border: esNuevo ? '1px solid #86efac' : (haCambiado ? '1px solid #fde047' : '1px solid #e2e8f0'),
                                background: esNuevo ? '#f0fdf4' : (haCambiado ? '#fefce8' : '#fff')
                              }}
                            >
                              <span style={{ color: '#1e293b' }}>
                                {it.descripcion} (x{it.cantidad || 1})
                                {esNuevo && <span style={{ marginLeft: 6, fontSize: '0.65rem', background: '#22c55e', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>NUEVO</span>}
                                {haCambiado && <span style={{ marginLeft: 6, fontSize: '0.65rem', background: '#eab308', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>MODIFICADO</span>}
                              </span>
                              <strong>${new Intl.NumberFormat('es-CL').format(it.monto || 0)}</strong>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
}
